import { Model } from '@vuex-orm/core'
import Users from 'src/api/users.js'
import { User, Channel } from 'src/store/models/index.js'
import { NewsChannel } from 'src/store/models/pivot/index.js'
import { modifyNewsStatsCache } from 'src/store/vue-axios/cacheStore.js'
import Channels from 'src/api/channels.js'
import GlobalPrototypes from '@/config/services/globalPrototypes.ts'

export class News extends Model {
  static entity = 'news'
  static fetchUrl = (id, page) => Users.newsFeedPaginatedUrl(id, page)

  static fields () {
    return {
      id: this.number(null),
      title: this.string(null).nullable(),
      content: this.string(''),
      content_rendered: this.string(''),
      updated_at: this.string(''),
      publish_at: this.attr(null),
      published_at: this.attr(null),
      attachments: this.attr([]),
      preview_image: this.number(-1),
      channels: this.belongsToMany(Channel, NewsChannel, 'news_id', 'channel_id'),
      channel_pivot: this.hasManyBy(NewsChannel, 'channel_id'),
      is_public: this.boolean(false),
      is_confirmable: this.boolean(false),
      is_notifiable: this.boolean(true),
      is_official: this.boolean(false),
      has_translations: this.boolean(false),
      has_emergency_sms: this.boolean(false),
      is_pinned: this.boolean(false),
      meta: this.attr({}),
      user: this.attr({}),
      user_id: this.attr(null),
      preview: this.attr(null),
      files: this.attr([]),
      user_ids: this.attr([]),
      users: this.hasManyBy(User, 'user_ids'),
      can: this.attr({}),
      survey_form_id: this.attr(null),
      survey_uuid: this.attr(null),
      survey: this.attr(null)
    }
  }

  static linkTo (news) {
    return {
      name: 'news-item',
      params: { id: news.id }
    }
  }

  static async fetchByDate (date) {
    const url = `${this.fetchUrl('self', 1)}&updated_at=${date}`
    const response = await GlobalPrototypes.axios().get(url, { showMessages: false })
    try {
      return response.data
    } catch {
      console.warn('news update has problem')
    }
  }

  static async checkScheduledNews (userId, page = 1) {
    // we are only interested in fresh data here,
    // but we need to cache it just in case user goes to scheduled news page
    const { network } = await Users.getNewsFeedPaginatedScheduled(userId, page)

    const res = await network()

    return res.data?.length
  }

  static async fetchByUser (params, userId = null) {
    const id = userId || 'self'

    const saveData = res => {
      const data = res.data
      if (params.page === 1) {
        this.deleteAll()
      }
      // watch out, ORM mutates here
      this.insertOrUpdate(GlobalPrototypes.clone({ data }))
    }

    const { data, network } = await GlobalPrototypes.cacheHttp(this.fetchUrl(id, params.page))

    saveData({ data })

    const res = await network()

    saveData(res)

    return res
  }

  static async clearFetchingCache (id) {
    if (typeof caches === 'undefined' || !caches) return
    const cache = await caches.open('custom')
    const buildUrl = url => `https://${(import.meta.env.VITE_API_URL + url).replace('https://', '').replace('//', '/')}`
    const fetchNewUrl = buildUrl(Users.newsFeedPaginatedUrl(id, 1))
    await cache.delete(fetchNewUrl)
  }

  static async confirmReadStatus (id) {
    return Channels.putChannelConfirmNewsById(id)
  }

  static async togglePin (id, data) {
    return Channels.putChannelPinNewsById(id, data)
  }

  static async notifyNews (newsId) {
    return Channels.postNewsNotifyById(newsId)
  }

  static async updateCachedStats (updateInfo, userId, pageNumber) {
    const url = this.fetchUrl(userId, pageNumber)
    await modifyNewsStatsCache(url, updateInfo.news_id, updateInfo.statistics)
  }
}

export default News
